export default function About() {
  const features = [
    'Affordable Pricing',
    'Fast Turnaround',
    'Ongoing Support',
    'Mobile-First Design',
    'SEO Optimized',
    'Easy to Update'
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="about" className="py-16 sm:py-20 lg:py-24 bg-white relative overflow-hidden">
      {/* Geometric Shapes Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-64 h-64 bg-cyan-500 rounded-full"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 transform rotate-45"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-6 sm:space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                <i className="ri-team-line"></i>
                About Outsmart Techs
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Your Partner in{' '}
                <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                  Digital Success
                </span>
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-3 sm:mb-4">
                We specialize in creating professional, affordable websites for small and medium businesses. Whether you're a local shop, service provider, or growing startup, we help you establish a strong online presence that attracts customers and drives growth.
              </p>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                No technical jargon, no hidden fees, just beautiful websites that work for your business. We handle everything from design to launch, so you can focus on what you do best.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button 
              onClick={scrollToContact}
              className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-gray-900 text-gray-900 text-sm sm:text-base font-semibold rounded-full hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Start Your Project
            </button>
          </div>

          {/* Right Image */}
          <div className="relative order-first lg:order-last">
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
              <div className="w-full h-[400px] sm:h-[500px] lg:h-[600px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Small%20business%20owner%20working%20with%20web%20designer%20on%20laptop%20in%20modern%20bright%20office%2C%20collaborative%20meeting%20discussing%20website%20design%20mockups%20on%20screen%2C%20friendly%20professional%20consultation%20atmosphere%20with%20coffee%20and%20notebooks%2C%20diverse%20entrepreneurs%20planning%20digital%20strategy%20together%2C%20natural%20lighting%20warm%20tones%2C%20high%20quality%208k%20photography&width=800&height=1000&seq=about-smb-001&orientation=portrait"
                  alt="Small Business Website Design Consultation"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Success Stories */}
        <div className="mt-16 sm:mt-20 text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="flex -space-x-3 sm:-space-x-4">
              {[
                'https://readdy.ai/api/search-image?query=Happy%20small%20business%20owner%20portrait%20smiling%20confidently%20in%20their%20shop%2C%20successful%20entrepreneur%20headshot%20with%20warm%20friendly%20expression%2C%20professional%20photography%20with%20natural%20lighting%20and%20clean%20background%2C%20diverse%20business%20professional%2C%20high%20quality%208k&width=80&height=80&seq=client-001&orientation=squarish',
                'https://readdy.ai/api/search-image?query=Satisfied%20small%20business%20owner%20portrait%20with%20genuine%20smile%2C%20successful%20local%20entrepreneur%20headshot%20looking%20confident%20and%20approachable%2C%20professional%20photography%20with%20soft%20lighting%20and%20neutral%20background%2C%20diverse%20business%20professional%2C%20high%20quality%208k&width=80&height=80&seq=client-002&orientation=squarish',
                'https://readdy.ai/api/search-image?query=Content%20small%20business%20owner%20portrait%20with%20warm%20smile%2C%20thriving%20entrepreneur%20headshot%20showing%20success%20and%20happiness%2C%20professional%20photography%20with%20natural%20lighting%20and%20simple%20background%2C%20diverse%20business%20professional%2C%20high%20quality%208k&width=80&height=80&seq=client-003&orientation=squarish',
                'https://readdy.ai/api/search-image?query=Pleased%20small%20business%20owner%20portrait%20smiling%20proudly%2C%20accomplished%20entrepreneur%20headshot%20with%20friendly%20demeanor%2C%20professional%20photography%20with%20bright%20lighting%20and%20clean%20background%2C%20diverse%20business%20professional%2C%20high%20quality%208k&width=80&height=80&seq=client-004&orientation=squarish'
              ].map((avatar, index) => (
                <div key={index} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 sm:border-4 border-white overflow-hidden">
                  <img src={avatar} alt={`Happy client ${index + 1}`} className="w-full h-full object-cover object-top" />
                </div>
              ))}
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 sm:border-4 border-white bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-sm sm:text-base font-bold">
                +10
              </div>
            </div>
          </div>
          <p className="text-sm sm:text-base text-gray-600 px-4">
            Join the group of small businesses that trust us with their online presence
          </p>
        </div>
      </div>
    </section>
  );
}
